/**
 * Utility functions for content processing and manipulation
 */

import { arrayMove } from 'react-sortable-hoc'

/**
 * Maps object properties by order using arrayMove
 * @param {Object} obj - The object to reorder
 * @param {number} oldIndex - The old index position
 * @param {number} newIndex - The new index position
 * @returns {Object} - The reordered object
 */
export const mapObjectByOrder = (obj, oldIndex, newIndex) => {
  const keys = Object.keys(obj)
  const newKeys = arrayMove(keys, oldIndex, newIndex)
  const newObj = {}
  newKeys.forEach(key => {
    newObj[key] = obj[key]
  })
  return newObj
}

/**
 * Converts JSON object to array of objects for API consumption
 * @param {Object} jsonObject - The content object
 * @returns {Array} - Array of course objects
 */
export const convertToArrayOfObjects = jsonObject => {
  const result = []

  for (const topic in jsonObject) {
    if (jsonObject.hasOwnProperty(topic)) {
      const sub_topics = Object.keys(jsonObject[topic])
      const courseObject = {
        topic,
        sub_topics: sub_topics.length === 0 ? [topic] : sub_topics
      }
      result.push(courseObject)
    }
  }

  return result
}

/**
 * Selects items with empty or error generated content
 * @param {Object} data - The content data
 * @returns {Object} - Filtered content with only empty/error items
 */
export function selectItemsWithGeneratedContentEmpty(data) {
  const selectedItems = {}

  for (const key in data) {
    const subtopics = data[key]
    for (const subtopicKey in subtopics) {
      const subtopic = subtopics[subtopicKey]
      if (
        !subtopic.generatedContent ||
        subtopic.generatedContent === '' ||
        (subtopic.status && subtopic.status === 'error')
      ) {
        if (!selectedItems[key]) {
          selectedItems[key] = {}
        }
        selectedItems[key][subtopicKey] = subtopic
      }
    }
  }

  return selectedItems
}

/**
 * Flattens JSON structure for document generation
 * @param {Object} data - The content data
 * @returns {Array} - Flattened array of content items
 */
export function flattenJSON(data) {
  const result = []

  function flattenObject(obj, parentType = 'title') {
    for (const key in obj) {
      const content = obj[key].generatedContent

      if (typeof obj[key] === 'object' && Object.keys(obj[key]).length > 0) {
        result.push({ type: parentType, content: key })
        flattenObject(obj[key], 'subtopic')
      }

      if (content) {
        result.push({ type: 'content', content })
      }
    }
  }

  flattenObject(data)

  return result
}

/**
 * Converts content to course data format
 * @param {Object} data - The content data
 * @returns {Array} - Array of course data objects
 */
export function convertContentToCourseData(data) {
  const dataArray = []

  for (const topic in data) {
    const subTopics = Object.entries(data[topic])
      .filter(([key, value]) => key !== 'generatedContent')
      .map(([sub_topic, { generatedContent, img_path }]) => ({
        sub_topic,
        generated_content: generatedContent,
        img_path
      }))
    dataArray.push({ topic, sub_topics: subTopics })
  }

  return dataArray
}

/**
 * Removes references from content
 * @param {Object} inputObj - The content object
 * @returns {Object} - Content without references
 */
export function removeReferences(inputObj) {
  // Remove references from each section
  const data = JSON.parse(JSON.stringify(inputObj))
  for (const section in data) {
    for (const subsection in data[section]) {
      const content = data[section][subsection].generatedContent
      let contentWithoutList = content.replace(/\nReferences:.*$/gs, '')

      // Remove references within the text
      let contentWithoutReferences = contentWithoutList.replace(
        /\[\d+\]/g,
        ''
      )

      contentWithoutReferences.trim()

      data[section][subsection].generatedContent = contentWithoutReferences
    }
  }

  // Convert the modified object back to JSON string and return
  return data
}

/**
 * Extracts references from content
 * @param {Object} data - The content data
 * @returns {Array} - Array of extracted references
 */
export function extractReferences(data) {
  const referencesSet = new Set()

  // Loop through each section
  Object.values(data).forEach(section => {
    // Loop through each sub-section
    Object.values(section).forEach(subSection => {
      // Extract references from generated content
      const generatedContent = subSection.generatedContent
      const regex = /\[([^\]]+)\] [^\[]+/g // Regular expression to match references
      let match
      while ((match = regex.exec(generatedContent)) !== null) {
        let reference = match[0]
        // Remove trailing newline characters
        reference = reference.replace(/\n$/, '')
        referencesSet.add(reference) // Add matched reference to the set
      }
    })
  })

  // Convert set to sorted array and return
  const references = Array.from(referencesSet).sort()
  return references
}
